import {Option} from 'platform/components';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetMakeModelWithMakeQuery} from '@dms/api/sales';
import {useGetTopMakesQuery} from '@dms/api/vehicle';
import i18n, {FALLBACK_LANGUAGE} from '@dms/i18n';

import {Nullish} from 'shared/web';

import {sortByLabel} from 'features/vehicle-catalogue';

import {useCustomTenantCatalogue} from './useCustomTenantCatalogue';

type GroupType = {
  label: string;
  options: readonly Option[];
};

type UseVehicleMakeReturnType = [
  {makeOptions: Option[] | Nullish; groupedMakeOptionsWithTopMakes: GroupType[] | Nullish},
  {isLoading: boolean; isError: boolean},
];

export function useVehicleMake(vehicleType: string | Nullish): UseVehicleMakeReturnType {
  const {data, isLoading, isError} = useGetMakeModelWithMakeQuery(
    {
      vehicleType: vehicleType ?? '',
      lang: [i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE],
    },
    {skip: isNilOrEmpty(vehicleType)}
  );

  const {
    customMakeOptions,
    isLoading: isCustomTenantCatalogueLoading,
    isError: isCustomTenantCatalogueErrored,
  } = useCustomTenantCatalogue(vehicleType);

  const {
    data: topMakes,
    isLoading: isTopMakesLoading,
    isError: isTopMakesErrored,
  } = useGetTopMakesQuery(undefined, {skip: isNilOrEmpty(vehicleType)});

  const baseOptions =
    data?.reduce((makes: Option[], make) => {
      if (isNilOrEmpty(make.make)) {
        return makes;
      }
      return [
        ...makes,
        {
          label: make.default_label,
          value: make.make,
        },
      ];
    }, []) ?? null;

  const baseMakeOptions: Option[] | Nullish =
    isNotNilOrEmpty(customMakeOptions) && isNotNilOrEmpty(baseOptions)
      ? [...customMakeOptions!, ...baseOptions!]
      : baseOptions;

  const makeOptions: Option[] | Nullish = sortByLabel(baseMakeOptions);

  const groupedMakeOptionsWithTopMakes =
    makeOptions && topMakes
      ? [
          {
            label: i18n.t('general.labels.topMakes'),
            options: makeOptions.filter((make) =>
              topMakes.find((topMake) => make.value === topMake.code)
            ),
          },
          {
            label: i18n.t('general.labels.allMakes'),
            options: makeOptions.filter(
              (make) => !topMakes.some((topMake) => make.value === topMake.code)
            ),
          },
        ]
      : null;

  return [
    {makeOptions, groupedMakeOptionsWithTopMakes},
    {
      isLoading: isLoading || isTopMakesLoading || isCustomTenantCatalogueLoading,
      isError: isError || isTopMakesErrored || isCustomTenantCatalogueErrored,
    },
  ];
}
